import React, { useCallback, useEffect, useMemo, useState } from "react";
import Popup from "../components/PopUP";
import Sidebar from "../components/Sidebar";
import Header from "../components/Header";
import TableFooter from "../components/TableFooter";
import DeleteModal from "../components/DeleteModal";
import api from "../Api";
import { IMAGE_URL , API_BASE_URL} from "../utils/constants";


const CHRONICLE_LIST_URL = `${API_BASE_URL}/triviagia/aviation-chronicle-details`;
const CHRONICLE_UPLOAD_URL = `${API_BASE_URL}/triviagia/upload-aviation-chronicle-file/`;
const CHRONICLE_DELETE_URL = `${API_BASE_URL}/triviagia/bulk-delete-aviation-chronicle/`;
const CHRONICLE_APPROVE_URL = `${API_BASE_URL}/triviagia/bulk-approve-aviation-chronicle/`;
const CHRONICLE_DISAPPROVE_URL = `${API_BASE_URL}/triviagia/bulk-disapprove-aviation-chronicle/`;

export default function UploadAviationChronicle() {
  const [glossaryDetails, setGlossaryDetails] = useState([]);
  const [glossaryResponse, setGlossaryResponse] = useState(false);
  const [loading, setLoading] = useState(false);
  const [popupMessage, setPopupMessage] = useState("");
  const [showModal, setShowModal] = useState(false);
  const [selectedItem, setSelectedItem] = useState(null);
  const [apiError, setApiError] = useState("");

  const [hasUploaded, setHasUploaded] = useState(false);

  const [pagination, setPagination] = useState({
    count: 0,
    total_pages: 0,
    next: null,
    previous: null,
  });
  const [currentPage, setCurrentPage] = useState(1);
  const [query, setQuery] = useState("");
  const [selectedGlossaryIds, setSelectedGlossaryIds] = useState([]);
  const [bulkActionLoading, setBulkActionLoading] = useState(false);

  const fetchGlossaryDetails = useCallback(async () => {
    try {
      const response = await api.get(
        `${CHRONICLE_LIST_URL}?q=${encodeURIComponent(query)}`
      );
      const data = response.data;

      const results = Array.isArray(data) ? data : data?.results;
      setGlossaryDetails(Array.isArray(results) ? results : []);
      setPagination({
        count: data?.count ?? (Array.isArray(results) ? results.length : 0),
        total_pages: data?.total_pages ?? 1,
        next: data?.next ?? null,
        previous: data?.previous ?? null,
      });
      setApiError("");
    } catch (error) {
      console.error("Aviation Chronicle API Error:", error);
      // setApiError("Failed to fetch Aviation Chronicle details. Check the API response.");
    }
  }, [query]);

  // useEffect(() => {
  //   let isMounted = true;
  //   const loadGlossaryDetails = async () => {
  //     setLoading(true);
  //     try {
  //       await fetchGlossaryDetails();
  //     } finally {
  //       if (isMounted) setLoading(false);
  //     }
  //   };
  //   loadGlossaryDetails();
  //   return () => {
  //     isMounted = false;
  //   };
  // }, [fetchGlossaryDetails]);

    useEffect(() => {
      const timer = setTimeout(async () => {
        try {
          await fetchGlossaryDetails();
        } finally {
          setLoading(false);
        }
      }, 200);
  
      return () => clearTimeout(timer);
    }, [fetchGlossaryDetails]);

  const handleSelectGlossary = (id) => {
    setSelectedGlossaryIds((previous) =>
      previous.includes(id) ? previous.filter((selectedId) => selectedId !== id) : [...previous, id]
    );
  };

  const handleSelectAllGlossary = (checked) => {
    const visibleIds = filteredGlossaryDetails.map((item) => item.id);
    setSelectedGlossaryIds((previous) =>
      checked
        ? [...new Set([...previous, ...visibleIds])]
        : previous.filter((id) => !visibleIds.includes(id))
    );
  };

  const handleBulkGlossaryAction = async (action) => {
    if (selectedGlossaryIds.length === 0) {
      setPopupMessage("Please select at least one Aviation Chronicle record.");
      return;
    }

    if (action === "delete") {
      setSelectedItem(null);
      setShowModal(true);
      return;
    }

    const endpoint = action === "approve" ? CHRONICLE_APPROVE_URL : CHRONICLE_DISAPPROVE_URL;

    try {
      setBulkActionLoading(true);
      const response = await api.patch(endpoint, { aviation_chronicle_ids: selectedGlossaryIds });
      setPopupMessage(
        response.data?.detail ||
        (action === "approve" ? "Aviation Chronicle approved successfully." : "Aviation Chronicle disapproved successfully.")
      );
      setSelectedGlossaryIds([]);
      await fetchGlossaryDetails();
    } catch (error) {
      console.error(`Aviation Chronicle bulk ${action} error:`, error);
      const detail = error.response?.data?.detail;
      setPopupMessage(typeof detail === "string" ? detail : `Failed to ${action} selected Aviation Chronicle records.`);
    } finally {
      setBulkActionLoading(false);
    }
  };

  const handleDeleteClick = (item) => {
    setSelectedItem(item);
    setShowModal(true);
  };

  const handleConfirmDelete = async () => {
    const glossaryIdsToDelete = selectedItem?.id ? [selectedItem.id] : selectedGlossaryIds;
    if (glossaryIdsToDelete.length === 0) return;

    try {
      setLoading(true);
      const response = await api.delete(CHRONICLE_DELETE_URL, {
        data: { aviation_chronicle_ids: glossaryIdsToDelete },
      });
      setShowModal(false);
      setSelectedItem(null);
      setSelectedGlossaryIds((previous) => previous.filter((id) => !glossaryIdsToDelete.includes(id)));
      setPopupMessage(response.data?.detail || "Aviation Chronicle deleted successfully.");
      setGlossaryDetails((previous) =>
        previous.filter((item) => !glossaryIdsToDelete.includes(item.id))
      );
    } catch (error) {
      console.error("Aviation Chronicle delete error:", error);
      const detail = error.response?.data?.detail;
      setPopupMessage(typeof detail === "string" ? detail : "Failed to delete Aviation Chronicle.");
    } finally {
      setLoading(false);
    }
  };


  const handleSubmit = async (event) => {
    event.preventDefault();
    setApiError("");

    const form = event.currentTarget;
    const file = form.elements.upload_csv?.files?.[0];

    if (!file) {
      setApiError("Please select a file.");
      return;
    }

    const formData = new FormData();
    formData.append("file", file, file.name);

    try {
      setLoading(true);

      const response = await api.post(CHRONICLE_UPLOAD_URL, formData, {
        withCredentials: true,
      });

      setPopupMessage(
        response.data?.detail || "Aviation Chronicle uploaded successfully."
      );

      setCurrentPage(1);
      setHasUploaded(true);
      setGlossaryResponse(true);
      setQuery("");
      form.reset();
    } catch (error) {
      console.error("Aviation Chronicle upload error:", error);

      const detail = error.response?.data?.detail;
      const errorMessage =
        typeof detail === "string"
          ? detail
          : detail?.message || "Failed to upload Aviation Chronicle file.";

      setApiError(errorMessage);
      setLoading(false);
    }
  };


  useEffect(() => {
    if (glossaryResponse) {
      const timer = setTimeout(async () => {
        try {
          await fetchGlossaryDetails();
        } finally {
          setGlossaryResponse(false);
          setLoading(false);
        }
      }, 2000);

      return () => clearTimeout(timer);
    }
  }, [glossaryResponse, fetchGlossaryDetails]);

  const filteredGlossaryDetails = useMemo(() => {
    const searchText = query.trim().toLowerCase();
    if (!searchText) return glossaryDetails;
    return glossaryDetails.filter((item) =>
      `${item.title ?? ""} ${item.description ?? ""}`.toLowerCase().includes(searchText)
    );
  }, [glossaryDetails, query]);

  return (
    <>
      <div className="user-management-page">
        <Popup message={popupMessage} onClose={() => setPopupMessage("")} />
        <Sidebar />
        <div className="main-wrapper">
          <Header />
          <div className="content-wrapper">
            <section className="main-section spacer-y">
              <div className="container">
                <div className="row gy-4">
                  <div className="col-xl-12">
                    <div className="common_main_bg_wrapper">
                      <div className="common_main_heading_wrapper">
                        <div className="vstack">
                          <nav className="breadcrumb_nav">
                            <ul className="breadcrumb mb-0" />
                          </nav>
                          <h1>Aviation Chronicle Ingestion</h1>
                        </div>
                      </div>
                    </div>

                    <form onSubmit={handleSubmit}>
                      <div className="row common-outer-card mx-2 my-5 p-3 justify-content-center">
                        <div className="col-12 col-md-6 col-lg-5">
                          <div className="form_field">
                            <label>Upload File here</label>
                            <div className="input_field">
                              <input
                                type="file"
                                className="form-control"
                                name="upload_csv"
                                accept=".csv, text/csv, application/vnd.ms-excel"
                                required
                              />
                              <span className="error">{apiError}</span>
                            </div>
                          </div>
                        </div>
                        <div className="col-12 col-md-6 col-lg-3 mt-3">
                          <button type="submit" className="secondary_btn mt-3" disabled={loading}>
                            {loading ? "Uploading..." : "Upload"}
                          </button>
                        </div>
                      </div>
                    </form>

                    {loading ? (
                      <div
                        className="d-flex justify-content-center align-items-center"
                        style={{ minHeight: "300px" }}
                      >
                        <div className="spinner-border text-primary" role="status">
                          <span className="visually-hidden">Loading...</span>
                        </div>
                      </div>
                    ) : (
                      hasUploaded && (
                      <div className="common-table-wrapper mt-4">
                        <div className="common-table-filter-wrapper">
                          <div className="common-left-blk">
                            <div className="common_sub_heading_wrapper mb-0">
                              <h2>Uploaded Aviation Chronicle Listing</h2>
                            </div>
                          </div>
                          <div className="common-right-blk">
                            <div className="d-flex flex-wrap align-items-center gap-2 mb-2">
                              <button type="button" className="secondary_btn" onClick={() => handleBulkGlossaryAction("approve")} disabled={bulkActionLoading || selectedGlossaryIds.length === 0}>
                                {bulkActionLoading ? "Please wait..." : "Approve"}
                              </button>
                              <button type="button" className="secondary_btn" onClick={() => handleBulkGlossaryAction("disapprove")} disabled={bulkActionLoading || selectedGlossaryIds.length === 0}>
                                Disapprove
                              </button>
                              <button type="button" className="secondary_btn" onClick={() => handleBulkGlossaryAction("delete")} disabled={bulkActionLoading || selectedGlossaryIds.length === 0}>
                                Delete
                              </button>
                            </div>
                            <div className="common-sort-blk">
                              <div className="common-search-blk">
                                <input
                                  type="search"
                                  className="form-control"
                                  id="glossary-search"
                                  placeholder="Search aviation chronicles..."
                                  value={query}
                                  onChange={(event) => setQuery(event.target.value)}
                                  name="glossary_search"
                                />
                              </div>
                            </div>
                          </div>
                        </div>

                        <div className="table-blk table-responsive">
                          <table className="table">
                            <thead>
                              <tr>
                                <th>
                                  <input
                                    type="checkbox"
                                    aria-label="Select all glossary records"
                                    checked={filteredGlossaryDetails.length > 0 && filteredGlossaryDetails.every((item) => selectedGlossaryIds.includes(item.id))}
                                    onChange={(event) => handleSelectAllGlossary(event.target.checked)}
                                  />
                                </th>
                                <th>S.No.</th>
                                <th>Title</th>
                                <th>Description</th>
                                <th>Status</th>
                                <th>Action</th>
                              </tr>
                            </thead>
                            <tbody>
                              {filteredGlossaryDetails.length > 0 ? (
                                filteredGlossaryDetails.map((item, index) => (
                                  <tr key={item.id}>
                                    <td>
                                      <input
                                        type="checkbox"
                                        aria-label={`Select ${item.title}`}
                                        checked={selectedGlossaryIds.includes(item.id)}
                                        onChange={() => handleSelectGlossary(item.id)}
                                      />
                                    </td>
                                    <td>{index + 1 + (currentPage - 1) * 20}</td>
                                    <td className="text-capitalize">{item.title}</td>
                                    <td>{item.description}</td>
                                    <td>
                                      {item.is_approved ? (
                                        <span className="badge bg-light text-success">Active</span>
                                      ) : (
                                        <span className="badge bg-light text-danger">Inactive</span>
                                      )}
                                    </td>
                                    <td>
                                      <button
                                        type="button"
                                        className="border-0 bg-white"
                                        onClick={() => handleDeleteClick({ id: item.id, name: item.title })}
                                        aria-label={`Delete ${item.title}`}
                                      >
                                        <img src={IMAGE_URL + "trash-icon.svg"} alt="Trash" />
                                      </button>
                                    </td>
                                  </tr>
                                ))
                              ) : (
                                <tr>
                                  <td colSpan={6} className="text-center">
                                    {apiError || "No aviation chronicle records found."}
                                  </td>
                                </tr>
                              )}
                            </tbody>
                          </table>
                        </div>

                        <TableFooter
                          pagination={pagination}
                          currentPage={currentPage}
                          setCurrentPage={setCurrentPage}
                        />
                      </div>
                      )
                    )}

                    <DeleteModal
                      show={showModal}
                      onClose={() => setShowModal(false)}
                      onConfirm={handleConfirmDelete}
                      itemName={selectedItem ? selectedItem.name : `${selectedGlossaryIds.length} selected aviation chronicle record(s)`}
                    />
                  </div>
                </div>
              </div>
            </section>
          </div>
        </div>
      </div>
    </>
  );
}
